import { Fragment, memo, useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useWindowSize } from 'react-use';
import StackGrid from 'react-stack-grid';
import cn from 'classnames';
import { CONTENT_TYPE, LSTORAGE_KEY } from 'src/constants';
import { IContent, IContentItemClass } from 'src/interface';
import { ImgUtils, LocalStore } from 'src/utils';
import { buildTrackingContent } from 'src/utils/tracking';
import { ALIGN_TYPE, List, LIST_TYPE } from 'src/components';
import Nossr from 'src/components/nossr';
import { ContentItem } from '../item';
import styles from './style.module.scss';

export interface IContentListProps {
    sectionId?: string;
    children?: React.ReactNode;
    className?: string;
    type?: LIST_TYPE;
    hasLayoutPhoto?: boolean;
    lazyloadImage?: boolean;
    displayTimeIn?: number;
    items: IContent[];
    align?: ALIGN_TYPE;
    alignItem?: ALIGN_TYPE;
    colMain?: boolean;
    ContentItemComponent?: React.FunctionComponent<any>;
    ContentItemProps?: any;
    ContentItemClass?: IContentItemClass;
    tracking?: string;
    typeList?: LIST_TYPE;
    isMobile?: boolean;
    itemSizeLimit?: number;
    domRef?: any;
}

export function ContentList({
    sectionId,
    className,
    type,
    hasLayoutPhoto = true,
    lazyloadImage = true,
    displayTimeIn,
    children,
    items = [],
    align,
    alignItem,
    ContentItemComponent = ContentItem,
    ContentItemProps = {},
    ContentItemClass = {},
    tracking = '',
    isMobile = true,
    itemSizeLimit,
    domRef,
}: IContentListProps) {
    const [contents, setContents] = useState<IContent[]>(
        items.slice(0, itemSizeLimit || items.length),
    );

    useEffect(() => {
        if ((window as any).renderSections) {
            setContents(items);
        } else {
            const itemIds = contents.map((item) => {
                return item.contentId;
            });
            const _items = items.filter((item) => {
                return !itemIds.includes(item.contentId);
            });
            setContents([...contents, ..._items]);
        }
    }, [items]);

    useEffect(() => {
        if (hasLayoutPhoto) {
            let latestTime: number =
                LocalStore.get(LSTORAGE_KEY.LATEST_READ) || 0;
            items.forEach((item) => {
                if (item.date > latestTime) {
                    latestTime = item.date;
                }
            });
            LocalStore.set(LSTORAGE_KEY.LATEST_READ, latestTime);
        }
    }, [items]);

    const itemInListUpdateFn = useCallback(
        (index: number, items: IContent[]) => {
            if (items.length) {
                const contentIds = contents.map((item) => {
                    return item.contentId;
                });

                let _items: IContent[] = [];
                items.forEach((item: IContent) => {
                    if (!contentIds.includes(item.contentId)) {
                        item.tracking = '|related_append';
                        _items.push(item);
                    }
                });

                if (_items.length) {
                    setContents(
                        contents
                            .slice(0, index + 1)
                            .concat(_items)
                            .concat(contents.slice(index + 1)),
                    );
                    (window as any).renderSections = false;
                }
            }
        },
        [contents, sectionId],
    );

    const renderItem = (
        content: IContent,
        index: number,
        tracking: string,
        showDescription: boolean = false,
        showCluster: boolean = false,
        ContentItemClass: IContentItemClass,
    ) => {
        const itemProps = Object.assign({}, ContentItemProps, {
            showDescription:
                ContentItemProps.showDescription || showDescription,
        });
        let _ContentItemClass = ContentItemClass;
        let classType: any = {};
        if (type === 'rank' && index === 0) {
            _ContentItemClass = Object.assign(
                {},
                {
                    CardClass: twMerge(
                        ContentItemClass.CardClass,
                        'max-md:flex-col',
                    ),
                    CardHeaderClass: twMerge(
                        ContentItemClass.CardHeaderClass,
                        'max-xxs:text-[2.2rem] max-xxs:leading-[26px]',
                        'xxs:max-xs:text-[2.4rem] xxs:max-xs:leading-[28px]',
                        'xs:max-md:text-[2.6rem] xs:max-md:leading-[30px]',
                    ),
                    CardImageClass: twMerge(
                        ContentItemClass.CardImageClass,
                        'w-full',
                        'max-md:pb-[55.4%]',
                        'max-md:!mt-0',
                    ),
                    CardFigureClass: ContentItemClass.CardFigureClass,
                    CardContentClass: twMerge(
                        ContentItemClass.CardContentClass,
                        'max-md:ml-0',
                    ),
                    CardFooterClass: twMerge(
                        ContentItemClass.CardFooterClass,
                        'max-md:pb-[3px]',
                    ),
                    ClusterClass: ContentItemClass.ClusterClass,
                },
            );
            content.thumb = ImgUtils.convertToRatioThumb(content.thumb, "w400_r3x2");
        }
        return (
            <Fragment key={`content.${content.contentId}.${index}`}>
                <ContentItemComponent
                    index={index}
                    item={content}
                    hasLayoutPhoto={hasLayoutPhoto && !(type === 'rank' && index === 0)}
                    displayTimeIn={displayTimeIn}
                    align={alignItem}
                    tracking={tracking}
                    lazyloadImage={lazyloadImage}
                    showCluster={showCluster}
                    itemInListUpdateFn={itemInListUpdateFn}
                    ContentItemClass={Object.assign(
                        {},
                        _ContentItemClass,
                        classType,
                    )}
                    {...itemProps}
                />
            </Fragment>
        );
    };

    const renderItems = useCallback(
        (
            showDescription: boolean = false,
            showCluster: boolean = false,
            ContentItemClass: IContentItemClass = {},
        ) => {
            return contents.map((content, index) => {
                if (!isMobile && content.type === CONTENT_TYPE.AD_BANNER) {
                    return null;
                }

                return renderItem(
                    content,
                    index,
                    buildTrackingContent(content.type, tracking, content.index),
                    showDescription,
                    showCluster,
                    ContentItemClass,
                );
            });
        },
        [contents],
    );

    if (isMobile) {
        return (
            <List
                className={cn(styles['content-list'], className, {
                    [styles[`is-${type}`]]: type,
                    [styles[`is-${align}`]]: align,
                })}
                domRef={domRef}
            >
                {children
                    ? children
                    : renderItems(false, false, ContentItemClass)}
            </List>
        );
    }

    return (
        <List
            className={cn(styles['content-grid'], className, {
                [styles[`is-${type}`]]: type,
                [styles[`is-${align}`]]: align,
            })}
        >
            <Nossr>
                <GridList renderItems={renderItems} />
            </Nossr>
        </List>
    );
}

function GridList({ renderItems }: { renderItems: any }) {
    const size = useWindowSize();
    const [isShow, setShow] = useState<boolean>(false);

    const ContentItemClass = Object.assign(
        {},
        {
            CardClass: twMerge(cn('flex w-full md:flex-col')),
            CardHeaderClass: twMerge(
                cn(
                    'text-[1.8rem] leading-[22px] font-semibold',
                    'xxs:text-[2rem] xxs:leading-[24px]',
                    'max-h-[96px]',
                    'overflow-hidden',
                    'md:max-h-none',
                ),
            ),
            CardImageClass: twMerge(
                cn(
                    'w-[33.33%] h-0 pb-[24.5%]',
                    'md:w-full md:h-auto md:pb-0 md:mt-0 md:!mb-0 md:rounded-none',
                ),
            ),
            CardFigureClass: 'md:!h-[150px]',
            CardVideoClass: 'md:!h-[150px]',
            CardContentClass: cn(
                'ml-[12px] pt-[4px]',
                'md:ml-0',
                'md:px-[8px] md:py-[10px]',
                'md:border md:border-solid md:border-[#ced4da] md:border-t-0',
                'md:bg-white',
            ),
            CardFooterClass: 'md:pb-[3px] md:h-[23px]',
            ClusterClass: cn(
                'hidden',
                'h-[36px] leading-[35px] px-[8px] text-[1.3rem]',
                'border border-solid border-[#ced4da] border-t-0',
                'bg-[#fdf7ea]',
                'md:block',
            ),
        },
    );

    useEffect(() => {
        setTimeout(() => {
            setShow(true);
        }, 50);
    }, []);

    return (
        <StackGrid
            className={twMerge(
                styles['grid-content'],
                '[&>:not(:first-child)]:max-md:mt-[12px] [&>:not(:first-child)]:max-md:pt-[12px]',
                '[&>:not(:first-child)]:max-md:border-t [&>:not(:first-child)]:max-md:border-solid [&>:not(:first-child)]:max-md:border-[#e9ecef]',
                'opacity-0',
                cn({
                    ['opacity-100']: isShow,
                }),
            )}
            duration={0}
            columnWidth={size.width < 768 ? '100%' : 230}
            gutterWidth={12}
            gutterHeight={12}
            appear={() => {}}
        >
            {renderItems(true, true, ContentItemClass)}
        </StackGrid>
    );
}
export default memo(ContentList);
